import React from 'react';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { defaultRateTypeFor } from '../../constants';

/**
 * Common top section of the Product Form Dialog.
 *
 * Renders brand name (with autocomplete dropdown), main category, subcategory
 * (with autocomplete dropdown), product name, grade + product-code prefix
 * (grade hidden for the "unstructured" categories), brand-description
 * (for Plywood-like categories), and the "show brand in customer PDF" toggle.
 *
 * All state and handlers come from the parent via props — this component is
 * purely presentational so the parent's mode-switching logic stays in one
 * place.
 */
export function CommonHeader({
  form,
  setForm,
  editingProduct,
  allCategories,
  brandSuggestions,
  showBrandDropdown,
  setShowBrandDropdown,
  handleBrandInput,
  selectBrand,
  subcatSuggestions,
  showSubcatDropdown,
  setShowSubcatDropdown,
  handleSubcatInput,
  selectSubcat,
  // Category-switch reset callbacks (owned by parent so all state resets
  // happen atomically when the admin changes category mid-form).
  setPlywoodSizes,
  setCustomSizes,
  setPlywoodRates,
  setCustomThicknesses,
  setAdhesiveRates,
  setAdhesiveRateTypes,
  setAdhesiveSecondaryRates,
  setCustomWeights,
}) {
  return (
    <>
      {/* Common fields: Brand and Category */}
      <div className="grid grid-cols-2 gap-3">
        {/* Brand Name with Autocomplete */}
        <div className="relative">
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Brand Name *</Label>
          <Input
            value={form.brand_name}
            onChange={e => handleBrandInput(e.target.value)}
            onBlur={() => setTimeout(() => setShowBrandDropdown(false), 200)}
            placeholder="Type brand name..."
            className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50"
            data-testid="form-brand-name"
          />
          {showBrandDropdown && brandSuggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-heda-line rounded-xl shadow-heda max-h-48 overflow-auto">
              {brandSuggestions.map(brand => (
                <div
                  key={brand}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-heda-cream border-b border-heda-line last:border-0"
                  onMouseDown={() => selectBrand(brand)}
                >
                  {brand}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Category *</Label>
          {editingProduct ? (
            // Disable category change for existing products
            <div className="mt-1 px-3 py-2 text-sm bg-heda-cream border border-heda-line rounded-xl text-heda-sub">
              {form.main_category}
              <span className="text-[11px] text-heda-mute ml-2">(cannot change)</span>
            </div>
          ) : (
            <Select
              value={form.main_category}
              onValueChange={v => {
                // New products only (editing locks the category): the rate
                // type follows the category like the rest of its state.
                setForm(f => ({ ...f, main_category: v, subcategory: '', rate_type: defaultRateTypeFor(v) }));
                // Reset ALL category-specific state when switching categories
                setPlywoodSizes([]);
                setCustomSizes([]);
                setPlywoodRates({});
                setCustomThicknesses([]);
                setAdhesiveRates({});
                setAdhesiveRateTypes({});
                setAdhesiveSecondaryRates({});
                setCustomWeights([]);
              }}
            >
              <SelectTrigger className="rounded-xl mt-1 text-sm bg-heda-cream/50" data-testid="form-category"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Common fields: Subcategory and Product Name */}
      <div className="grid grid-cols-2 gap-3">
        <div className="relative">
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Subcategory (e.g., CLUB PRIME) *</Label>
          <Input
            value={form.subcategory}
            onChange={e => handleSubcatInput(e.target.value)}
            onBlur={() => setTimeout(() => setShowSubcatDropdown(false), 200)}
            placeholder={form.main_category ? 'Type subcategory...' : 'Select category first'}
            className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50"
            data-testid="form-subcategory"
          />
          {showSubcatDropdown && subcatSuggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-heda-line rounded-xl shadow-heda max-h-48 overflow-auto">
              {subcatSuggestions.map(subcat => (
                <div
                  key={subcat}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-heda-cream border-b border-heda-line last:border-0"
                  onMouseDown={() => selectSubcat(subcat)}
                >
                  {subcat}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Product Name</Label>
          <Input value={form.product_name} onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))} className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="form-product-name" />
        </div>
      </div>

      {/* Common fields: Grade and Product Code - Hide Grade for certain categories */}
      {['Adhesives', 'Hardware', 'Screws/Patti', 'Wooden Flooring', 'Veneers', 'Highlighters', 'Laminates'].includes(form.main_category) ? (
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Product Code Prefix</Label>
          <Input value={form.product_code} onChange={e => setForm(f => ({ ...f, product_code: e.target.value }))} placeholder="e.g., CP" className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="form-product-code" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Grade</Label><Input value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))} placeholder="e.g., BWP, MR, BWR" className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="form-grade" /></div>
          <div><Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Product Code Prefix</Label><Input value={form.product_code} onChange={e => setForm(f => ({ ...f, product_code: e.target.value }))} placeholder="e.g., CP" className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="form-product-code" /></div>
        </div>
      )}

      {/* Brand Description - for Plywood, Blockboard, MDF, Doors (shown in PDF) */}
      {['Plywood', 'Blockboard', 'MDF', 'Doors', 'WPC/PVC'].includes(form.main_category) && (
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Brand Description (shown in PDF)</Label>
          <Input
            value={form.brand_description || ''}
            onChange={e => setForm(f => ({ ...f, brand_description: e.target.value }))}
            placeholder="e.g., EVEREST EXCEL BWP 710 PLYWOOD (21 YEARS WARRANTY)"
            className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50"
            data-testid="form-brand-description"
          />
          <p className="text-[11px] text-heda-mute mt-1">This description will appear as a header row in the quotation PDF</p>
        </div>
      )}

      {/* Per-product brand visibility — Admin/Manager decides whether the
          brand name appears in the CUSTOMER PDF. Defaults to ON so branded
          items (Tandem, Hettich, Hafele) stay visible; turn OFF for generic
          items like zigzag pins where the brand is meaningless to the
          customer. */}
      <div className="flex items-start gap-2 p-3 rounded-xl border border-heda-line bg-heda-cream">
        <input
          id="show-brand-in-pdf"
          type="checkbox"
          checked={form.show_brand_in_pdf !== false}
          onChange={e => setForm(f => ({ ...f, show_brand_in_pdf: e.target.checked }))}
          className="mt-0.5 w-4 h-4 accent-heda-olive"
          data-testid="form-show-brand-in-pdf"
        />
        <label htmlFor="show-brand-in-pdf" className="text-xs leading-tight cursor-pointer select-none">
          <span className="font-semibold text-heda-ink">Show brand name in customer PDF</span>
          <p className="text-heda-sub text-[10px] mt-0.5">
            Turn off for generic items (e.g., zigzag pins, common screws) where the brand
            doesn&apos;t matter to the customer. Keep on for branded items like Tandem, Hettich,
            Hafele, etc.
          </p>
        </label>
      </div>
    </>
  );
}
